import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { CoreService } from './core/core.service';
import { AuthService } from './auth/auth.service';
import { TokenManagerService } from './auth/token-manager.service';
import { parseJwt } from '../core/auth/jwt-helpers';

export const authGuard: CanActivateFn = async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const router = inject(Router);
  const coreService = inject(CoreService);
  const authService = inject(AuthService);
  const tokenManager = inject(TokenManagerService);

  try {
    // Obtener el recurso actual desde la ruta
    const resourceName = route.routeConfig?.path;

    // 1. Verificar que existe un token de acceso
    const accessToken = authService.getAccessToken();

    if (!accessToken) {
      console.warn('🔒 AuthGuard: No hay token de acceso. Redirigiendo a login.');
      router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
      return false;
    }

    // 2. Verificar que el token no esté expirado
    try {
      const payload = parseJwt(accessToken);
      const now = Math.floor(Date.now() / 1000);

      if (payload.exp < now) {
        console.warn('🔒 AuthGuard: Token expirado. Redirigiendo a login.');
        authService.clearSession();
        router.navigate(['/login'], {
          queryParams: {
            returnUrl: state.url,
            reason: 'token_expired',
          },
        });
        return false;
      }

      // 3. Verificar si el token está próximo a expirar y refrescarlo
      if (authService.isTokenAboutToExpire(accessToken, 600)) {
        console.log('🔄 AuthGuard: Token próximo a expirar. Intentando refrescar...');
        try {
          await authService.refreshTokens();
          console.log('AuthGuard: Token refrescado exitosamente.');
        } catch (refreshError) {
          console.error('❌ AuthGuard: Error al refrescar token:', refreshError);

          const latestAccessToken = authService.getAccessToken();
          if (latestAccessToken) {
            const latestPayload = parseJwt(latestAccessToken);
            const latestNow = Math.floor(Date.now() / 1000);
            if (latestPayload.exp > latestNow) {
              console.warn('⚠️ AuthGuard: Refresh falló, pero el token aún es válido. Continuando navegación.');
            } else {
              authService.clearSession();
              router.navigate(['/login'], {
                queryParams: {
                  returnUrl: state.url,
                  reason: 'refresh_failed',
                },
              });
              return false;
            }
          } else {
            authService.clearSession();
            router.navigate(['/login'], {
              queryParams: {
                returnUrl: state.url,
                reason: 'refresh_failed',
              },
            });
            return false;
          }
        }
      }
    } catch (decodeError) {
      console.error('❌ AuthGuard: Error al decodificar token:', decodeError);
      authService.clearSession();
      router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
      return false;
    }

    // 4. Para rutas específicas, verificar permisos de recurso
    if (!resourceName) {
      router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
      return false;
    }

    // 5. Excepción para la ruta 'home' - solo requiere autenticación
    if (resourceName === 'home') {
      return true;
    }

    // 6. Para rutas de administración, verificar permisos específicos
    if (resourceName.includes('administration')) {
      const hasAccess = coreService.hasAccessToResource(resourceName);

      if (hasAccess) {
        return true;
      } else {
        // Si tiene token válido pero no permisos específicos cargados, permitir acceso temporal
        console.warn('⚠️ AuthGuard: Permisos específicos no cargados aún. Permitiendo acceso temporal.');
        return true;
      }
    }

    // 7. Para otras rutas, verificar acceso a recursos
    const hasAccess = coreService.hasAccessToResource(resourceName);

    if (hasAccess) {
      return true;
    } else {
      // Si tiene token válido pero no se han cargado las membresías, permitir acceso
      console.warn('⚠️ AuthGuard: Membresías no cargadas. Permitiendo acceso temporal.');
      return true;
    }
  } catch (error: any) {
    console.error('❌ AuthGuard: Error inesperado:', error);
    // Redirigir al login en caso de error crítico
    router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
    return false;
  }
};
